import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "../../../supabase/supabase";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Loader2,
  MessageSquare,
  Code,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Transcript } from "@/types/prompt";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ChatLikeTranscript from "./ChatLikeTranscript";

export default function TranscriptDetail() {
  const { id } = useParams<{ id: string }>();
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("chat");
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchTranscript(id);
    }
  }, [id]);

  const fetchTranscript = async (transcriptId: string) => {
    try {
      setLoading(true);

      // Fetch the transcript
      const { data, error } = await supabase
        .from("transcripts")
        .select("*")
        .eq("id", transcriptId)
        .single();

      if (error) throw error;
      setTranscript(data);
    } catch (error) {
      console.error("Error fetching transcript:", error);
      toast({
        title: "Error",
        description: "Failed to load transcript. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrompt = () => {
    if (transcript) {
      navigate(`/prompt-engine/create?transcriptId=${transcript.id}`);
    }
  };

  if (loading) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p>Loading transcript...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!transcript) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">Transcript not found.</p>
            <Button className="mt-4" onClick={() => navigate("/prompt-engine")}>
              Back to Transcripts
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center mb-2">
          <Button
            variant="ghost"
            size="sm"
            className="mr-2 -ml-2"
            onClick={() => navigate("/prompt-engine")}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl">
              {transcript.name || `Transcript ${transcript.id.substring(0, 8)}`}
            </CardTitle>
            <CardDescription>
              Created on {new Date(transcript.created_at).toLocaleDateString()}
            </CardDescription>
          </div>
          <Button 
          variant="primaryOutline"
          onClick={handleCreatePrompt}>
            <FileText className="h-4 w-4 mr-1" /> Create Prompt
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="chat" className="flex items-center">
              <MessageSquare className="h-4 w-4 mr-2" /> Chat View
            </TabsTrigger>
            <TabsTrigger value="raw" className="flex items-center">
              <Code className="h-4 w-4 mr-2" /> Raw Text
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chat" className="mt-4">
            <ChatLikeTranscript transcript={transcript} maxHeight="500px" />
          </TabsContent>

          <TabsContent value="raw" className="mt-4">
            <ScrollArea className="border rounded-md p-4 h-[500px]">
              <pre className="whitespace-pre-wrap font-mono text-sm">
                {transcript.content}
              </pre>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button variant="outline" onClick={() => navigate("/prompt-engine")}>
          Back to Transcripts
        </Button>
      </CardFooter>
    </Card>
  );
}
